import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import parseDomain from 'parse-domain';
import { guid } from '@/mixins/guid';
import { formatDate } from '@/mixins/format-date';

export default Vue.component('edit-record', {
  template: require('./template.html'),
  name: 'edit-record',
  mixins: [formatDate, guid],
  props: ['current-domain', 'modal-content', 'statuses'],
  computed: {
    ...mapGetters(['priceSource', 'currentUser', 'currentSite', 'editDomainOpen']),
    records() {
      const d = this.currentDomain;
      return _.chain(d.records)
        .thru((t) => {
          if (this.filterKey) {
            return _.reduce(
              t,
              (acc, row) => {
                // only gather the cols the table needs from data
                const e = [];
                _.forEach(this.cols, (c) => {
                  const text = this.prepVal(row[c]);
                  e.push(text);
                });
                if (
                  _.filter(e, v =>
                    _(v)
                      .toLower()
                      .includes(this.filterKey.toLowerCase()),
                  ).length
                ) {
                  acc.push(row);
                }
                return acc;
              },
              [],
            );
          }
          return t;
        })
        .value();
    },
    getPlaceholder() {
      const defaultText = 'Points To Value';
      const formats = {
        A: 'ex: 192.168.0.1',
        AAAA: 'ex: 2001:0db8:85a3:0:0:8a2e:0370:7334',
        CNAME: `ex: ${this.placeholder.host}`,
        MX: 'ex: 10 mail.example.com',
        TXT: 'ex: "v=spf1 ip4:192.168.0.1/16 -all"',
        SRV: 'ex: 10 5 80 hostname.example.com',
      };
      return formats[this.selectedType] || defaultText;
    },
    hasMX() {
      return _.find(this.currentDomain.records, r => r.type === 'MX');
    },
    hasTXT() {
      return _.find(this.currentDomain.records, r => r.type === 'TXT');
    },
    hasSRV() {
      return _.find(this.currentDomain.records, r => r.type === 'SRV');
    },
    hasAAAA() {
      return _.find(this.currentDomain.records, r => r.type === 'AAAA');
    },
    canAddRecord() {
      const omitted = this.selectedType !== 'MX' &&
        this.selectedType !== 'TXT' &&
        this.selectedType !== 'SRV' &&
        this.selectedType !== 'AAAA';
      const mx = this.selectedType === 'MX' && !this.hasMX;
      const txt = this.selectedType === 'TXT' && !this.hasTXT;
      const srv = this.selectedType === 'SRV' && !this.hasSRV;
      const aaaa = this.selectedType === 'AAAA' && !this.hasAAAA;
      return mx || txt || srv || aaaa || omitted;
    },
  },
  data() {
    return {
      cols: ['domain', 'type', 'ttl', 'values'],
      addLoading: false,
      raLoading: false, // for record actions
      confirmDelete: null,
      paginate: ['domainlist'],
      filterKey: '',
      activeRecord: null,
      showTypes: false,
      selectedType: 'none',
      host: '',
      pointsto: '',
      ttl: '',
      awsErrors: false,
      regexErrors: false,
      recordValidators: {
        ipv4: /^(?=\d+\.\d+\.\d+\.\d+$)(?:(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]|[0-9])\.?){4}$/,
        ipv6: /^((?:[0-9A-Fa-f]{1,4}))((?::[0-9A-Fa-f]{1,4}))*::((?:[0-9A-Fa-f]{1,4}))((?::[0-9A-Fa-f]{1,4}))*|((?:[0-9A-Fa-f]{1,4}))((?::[0-9A-Fa-f]{1,4})){7}$/,
        txt: /^".*"$/,
      },
      typeOptions: [],
      recordData: [],
      editType: null,
      placeholder: {
        host: '',
        point: '',
      },
    };
  },
  created() {
    this.typeOptions = _.filter(this.priceSource.allowedRecords, r => r.type !== 'A');
    this.resetLocals();
    this.setLocalData();
  },
  watch: {
    selectedType() {
      this.awsErrors = false;
      if (this.pointsto !== '') {
        this.validateRecordType();
      } else {
        this.regexErrors = false;
      }
    },
  },
  methods: {
    resetLocals() {
      this.selectedType = 'none';
      this.showTypes = false;
      this.editType = null;
    },
    setLocalData() {
      this.recordData = [];
      _.forEach(this.currentDomain.records, (r, i) => {
        this.recordData.push({
          _id: i,
          awsError: false,
          host: '',
          pointsto: '',
          ttl: '',
        });
      });
    },
    getWikiLink() {
      const links = {
        A: 'a-record',
        AAAA: 'aaaa-record',
        CNAME: 'cname-record',
        MX: 'mx-record',
        SRV: 'srv-record',
        TXT: 'txt-record',
      };
      return `For help with formatting <a href="https://wiki.drzzle.app/domains/records/#${links[this.selectedType]}" target="_blank" class="adminUi-error-link">click here</a>`;
    },
    validateRecordType() {
      const records = {
        A: 'aValidate',
        AAAA: 'aaaaValidate',
        CNAME: 'cnameValidate',
        MX: 'mxValidate',
        SRV: 'srvValidate',
        TXT: 'txtValidate',
      };
      const validator = records[this.selectedType];
      if (validator) {
        this[validator](this.pointsto);
      }
    },
    txtValidate(value) {
      this.regexErrors = false;
      const quotes = this.recordValidators.txt;
      const error = `TXT records should be enclosed with quotes. ${this.getWikiLink()}.`;
      if (!value.match(quotes)) {
        this.regexErrors = error;
      }
    },
    cnameValidate(value) {
      this.regexErrors = false;
      const error = `CNAME records must be a valid domain. ${this.getWikiLink()}.`;
      const domain = parseDomain(value);
      if (!domain) {
        this.regexErrors = error;
      }
    },
    aaaaValidate(value) {
      this.regexErrors = false;
      const validIp = this.recordValidators.ipv6;
      const error = `AAAA records must be a valid IPv6 address. ${this.getWikiLink()}.`;
      if (!value.match(validIp)) {
        this.regexErrors = error;
      }
    },
    aValidate(value) {
      this.regexErrors = false;
      const error = `A records must be a valid IPv4 address. ${this.getWikiLink()}.`;
      const validIp = this.recordValidators.ipv4;
      if (!value.match(validIp)) {
        this.regexErrors = error;
      }
    },
    srvValidate(value) {
      this.regexErrors = false;
      const error = `SRV records must be formatted starting with the priority, weight and port numbers followed by a domain. ${this.getWikiLink()}.`;
      const items = value.split(/\s{1,}/);
      if (items.length !== 4) {
        this.regexErrors = error;
        return;
      }
      const num = /^\d+$/;
      const priority = items[0].trim();
      const weight = items[1].trim();
      const port = items[2].trim();
      if (!priority.match(num) || !weight.match(num) || !port.match(num)) {
        this.regexErrors = error;
        return;
      }
      const domain = parseDomain(items[3]);
      if (!domain) {
        this.regexErrors = error;
      }
    },
    mxValidate(value) {
      this.regexErrors = false;
      const error = `MX records must be formatted starting with the priority number followed by a domain. If you need multiple groups, separate by commas. ${this.getWikiLink()}.`;
      const values = value.split(/,\s{1,}/);
      _.forEach(values, (record) => { // eslint-disable-line
        const items = record.split(' ');
        if (items.length !== 2) {
          this.regexErrors = error;
          return false;
        }
        if (!items[0].match(/^\d+$/)) {
          this.regexErrors = error;
          return false;
        }
        const domain = parseDomain(items[1]);
        if (!domain) {
          this.regexErrors = error;
          return false;
        }
      });
    },
    async addRecord() {
      // we can stop the addition attempt first if the record does not meet validation
      if (this.regexErrors) {
        return;
      }
      // we only want to validate the "add" specific fields and not
      // the entire compontents inputs.. stupid vee validate
      const fields = [{ name: 'ttl', data: this.ttl }, { name: 'pointsTo', data: this.pointsto }];
      const checks = [];
      _.forEach(fields, (field) => {
        checks.push(this.$validator.validate(field.name, field.data));
      });
      Promise.all(checks).then(async (v) => {
        const check = v.indexOf(false);
        if (check === -1) {
          // All is passed so proceed
          this.awsErrors = false;
          this.addLoading = true;
          const host =
            this.host === '' || this.host === '@' ? `${this.currentDomain.name}${this.currentDomain.tld}` : this.host;
          // what we send to AWS
          const record = {
            type: this.selectedType,
            domain: host.trim(),
            data: parseDomain(host.trim()),
            ttl: this.ttl,
            hostedZoneId: this.currentDomain.hostedZoneId,
            values: this.getRecordValues(),
          };
          try {
            const update = await this._SEND([{
              name: 'touchDomainRecord',
              payload: {
                record,
                currentUser: _.cloneDeep(this.currentUser),
                currentSite: {
                  _id: this.currentSite._id,
                  alias: this.currentSite.alias,
                  name: this.currentSite.name,
                  cloudFront: this.currentSite.cloudFront,
                },
                currentTime: this.formatDate(Date.now()),
                domainData: this.currentDomain,
                target: this.pointsto,
              },
            }]);

            // add local data
            this.recordData.push({
              _id: this.recordData.length,
              awsError: false,
              host: '',
              pointsto: '',
              ttl: '',
            });

            // save to state
            const p = _.omit(_.cloneDeep(record), ['hostedZoneId', 'data']);
            p._id = this.guid();

            this.pointsto = '';
            this.resetLocals();
            this.$store.dispatch('addDomainRecord', {
              currentDomain: this.currentDomain,
              newRecord: p,
            });

            if (update.newCert) {
              // update domain with the queued state
              this.$store.dispatch('setGlobalValue', {
                item: this.currentDomain,
                key: 'status',
                value: 'RECORD_IN_PROGRESS',
              });
            }

            await this._SEND([{
              name: 'updateSiteAttr',
              payload: {
                siteId: this.currentSite._id,
                attr: 'domains',
                value: this.currentSite.domains,
              },
            }]);

            this.$log.debug('updateSiteAttr: domains');

            this.$emit('emit-check-domains');

            // finally, trigger success notification
            this.$store.dispatch('addNotification', {
              id: this.guid(),
              type: 'success',
              message: `Successfully added record for ${record.domain}!`,
            });
            this.$log.debug(`successfully created ${this.selectedType} record for new new ${host}: `, record);
          } catch (e) {
            this.awsErrors = `${e.message || e}. This could be because you have entered an incorrect format. For help with formatting <a href="https://wiki.drzzle.app/domains/records/" target="_blank" class="adminUi-error-link">click here</a>.`;
            this.$log.error(e);
            await this._reportError({
              error: e,
              payload: record,
              fn: 'addRecord',
            });
          } finally {
            this.addLoading = false;
            this._trackEvent({
              type: 'add_record',
              meta: {
                site_name: this.currentSite.alias,
                site_type: this.currentSite.type,
                site_tier: this.currentSite.tier,
                type: this.selectedType,
                domain: host.trim(),
                data: parseDomain(host.trim()),
                ttl: this.ttl,
                hosted_zone_id: this.currentDomain.hostedZoneId,
                values: this.getRecordValues(),
              },
            });
          }
        }
      });
    },
    getRecordValues(v) {
      const val = v || this.pointsto;
      const values = [];
      _.forEach(val.split(','), (Value) => {
        values.push({
          Value,
        });
      });
      return values;
    },
    showInputs(e) {
      this.selectedType = e.target.value;
      this.setPlaceholder();
    },
    setPlaceholder() {
      this.placeholder.host = `${this.currentDomain.name}${this.currentDomain.tld}`;
      this.host = this.placeholder.host;
    },
    cancelAdd() {
      this.showTypes = false;
      this.selectedType = 'none';
      this.host = '';
      this.pointsto = '';
      this.ttl = '';
      this.awsErrors = false;
    },
    realIndex(record) {
      return _.findIndex(this.currentDomain.records, r => r._id === record._id);
    },
    confirmDeleteRecord(record) {
      this.confirmDelete = this.realIndex(record);
      this.activeRecord = null;
      this.caLoading = false;
    },
    editRecord(record) {
      const index = this.realIndex(record);
      const v = this.prepVal(record.values);
      this.recordData[index].host = record.domain;
      this.recordData[index].pointsto = v;
      this.recordData[index].ttl = record.ttl;
      this.activeRecord = index;
      this.editType = record.type;
    },
    cancelEditRecord() {
      this.activeRecord = null;
      this.editType = null;
    },
    sanitizeNum(e, r) {
      const filtered = e.target.value.replace(/[^0-9]/g, '');
      if (r) {
        this.recordData[this.realIndex(r)].ttl = filtered;
      } else {
        this.ttl = filtered;
      }
    },
    pointsToMsg(str) {
      return str.replace(/(points-to-.*?)(?=\s|$)/g, 'points to');
    },
    ttlMsg(str) {
      return str.replace(/(ttl-.*?)(?=\s|$)/g, 'ttl');
    },
    saveRecordChanges(record) {
      // we only want to validate the specific fields and not the entire
      // compontents inputs.. stupid vee validate
      const fields = [
        {
          name: `ttl-${record._id}`,
          data: this.recordData[this.realIndex(record)].ttl,
        },
        {
          name: `points-to-${record._id}`,
          data: this.recordData[this.realIndex(record)].pointsto,
        },
      ];
      const checks = [];
      _.forEach(fields, (field) => {
        checks.push(this.$validator.validate(field.name, field.data));
      });
      Promise.all(checks).then(async (v) => {
        const check = v.indexOf(false);
        if (check === -1) {
          // All is passed so proceed
          const index = this.realIndex(record);
          this.activeRecord = null;
          this.confirmDelete = null;
          this.recordData[index].awsError = false;
          // gather new input
          let host = this.recordData[index].host;
          host = host === '' || host === '@' ? `${this.currentDomain.name}${this.currentDomain.tld}` : host;

          // what we send to AWS
          const payload = {
            action: 'UPSERT',
            type: record.type,
            domain: host.trim(),
            data: parseDomain(host.trim()),
            ttl: this.recordData[index].ttl,
            hostedZoneId: this.currentDomain.hostedZoneId,
            values: this.getRecordValues(this.recordData[index].pointsto),
          };

          // only send if there are changes
          const changes =
            record.domain !== payload.domain ||
            record.ttl.toString() !== payload.ttl.toString() ||
            !_.isEqual(record.values, payload.values);

          if (changes) {
            this.raLoading = index;
            try {
              const update = await this._SEND([{
                name: 'touchDomainRecord',
                payload: {
                  record: payload,
                  currentUser: _.cloneDeep(this.currentUser),
                  currentSite: {
                    _id: this.currentSite._id,
                    alias: this.currentSite.alias,
                    name: this.currentSite.name,
                    cloudFront: this.currentSite.cloudFront,
                  },
                  currentTime: this.formatDate(Date.now()),
                  domainData: this.currentDomain,
                  target: this.recordData[index].pointsto,
                },
              }]);

              // update state
              const p = _.omit(payload, ['hostedZoneId', 'action', 'data']);
              this.$store.dispatch('updateDomainRecord', {
                currentDomain: this.currentDomain,
                newRecord: p,
                index,
              });

              if (update.newCert) {
                // update domain with the queued state
                this.$store.dispatch('setGlobalValue', {
                  item: this.currentDomain,
                  key: 'status',
                  value: 'RECORD_IN_PROGRESS',
                });
              }

              // save to db
              await this._SEND([{
                name: 'updateSiteAttr',
                payload: {
                  siteId: this.currentSite._id,
                  attr: 'domains',
                  value: this.currentSite.domains,
                },
              }]);

              this.$log.debug('updateSiteAttr: domains');

              this.$emit('emit-check-domains');

              // finally trigger success notification
              this.$store.dispatch('addNotification', {
                id: this.guid(),
                type: 'success',
                message: `Successfully edited record for ${payload.domain}!`,
              });

              this.$log.debug('successfully edited record: ', payload);
            } catch (e) {
              this.recordData[index].awsError = e;
              this.$log.error('error: ', e);
              await this._reportError({
                error: e,
                fn: 'saveRecordChanges',
              });
            } finally {
              this.raLoading = false;
            }
          }
        }
      });
    },
    prepVal(v) {
      let text = v;
      if (_.isArray(v)) {
        text = v.map(e => e.Value).join(', ');
      }
      return text;
    },
    highlight(ctx, query) {
      const c = ctx.toString();
      return c.replace(
        new RegExp(query, 'ig'),
        match => `<span class='editorUi-searchHighlight'>${match}</span>`,
      );
    },
    async deleteRecord(record) {
      const index = this.realIndex(record);
      this.raLoading = index;
      this.activeRecord = null;
      this.confirmDelete = null;
      this.recordData[index].awsError = false;
      try {
        const payload = _.cloneDeep(record);
        payload.hostedZoneId = this.currentDomain.hostedZoneId;
        payload.action = 'DELETE';

        await this._SEND([{
          name: 'touchDomainRecord',
          payload: {
            record: payload,
            currentUser: _.cloneDeep(this.currentUser),
            currentSite: {
              _id: this.currentSite._id,
              alias: this.currentSite.alias,
              name: this.currentSite.name,
              cloudFront: this.currentSite.cloudFront,
            },
            currentTime: this.formatDate(Date.now()),
            domainData: this.currentDomain,
            target: this.recordData[index].pointsto,
          },
        }]);

        // remove from state
        await this.$store.dispatch('removeDomainRecord', {
          currentDomain: this.currentDomain,
          record,
          index,
        });

        // remove local data
        this.recordData.splice(index, 1);

        // save to db
        await this._SEND([{
          name: 'updateSiteAttr',
          payload: {
            siteId: this.currentSite._id,
            attr: 'domains',
            value: this.currentSite.domains,
          },
        }]);

        this.$log.debug('updateSiteAttr: domains');

        // finally trigger success notification
        this.$store.dispatch('addNotification', {
          id: this.guid(),
          type: 'success',
          message: `Successfully deleted record for ${payload.domain}!`,
        });

        this.editType = null;

        this.$log.debug('successfully deleted record: ', payload);

        // handle pagination if none left on list
        if (this.paginate.domainlist.list.length === 0) {
          let lastPage = this.paginate.domainlist.page - 1;
          lastPage = lastPage < 0 ? 0 : lastPage;
          this.paginate.domainlist.page = lastPage;
        }
      } catch (e) {
        this.recordData[index].awsError = e;
        this.$log.error('error: ', e);
        await this._reportError({
          error: e,
          fn: 'deleteRecord',
        });
      } finally {
        this.raLoading = null;
      }
    },
    dontDelete(record) {
      this.confirmDelete = null;
      this.activeRecord = this.realIndex(record);
    },
    formatErrors(errs) {
      return this.seperateCamalCase(errs.join(' '));
    },
    seperateCamalCase(str) {
      return str.replace(/([A-Z])/g, g => ` ${g[0].toLowerCase()}`);
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/admin/websites/edit-site/domains/edit-domain/edit-record/index.js